import React, { useState, useRef, useMemo, useEffect } from 'react';
import { Quest, Program } from '../../types';
import { useLanguage } from '../../contexts/LanguageContext';

interface WeekViewProps {
  currentDate: Date;
  quests: Quest[];
  programs?: Program[];
  onQuestClick: (quest: Quest) => void;
  onTimeSlotClick: (date: string, time: string) => void;
  onQuestDragEnd: (questId: string, newDate: string, newStartTime: string) => void;
  onQuestResize?: (questId: string, newStartTime: string, newEndTime: string) => void;
}

const WeekView: React.FC<WeekViewProps> = ({
  currentDate,
  quests,
  programs = [],
  onQuestClick,
  onTimeSlotClick,
  onQuestDragEnd,
  onQuestResize,
}) => {
  const { t } = useLanguage();
  const timelineRef = useRef<HTMLDivElement>(null);
  const [draggedQuest, setDraggedQuest] = useState<Quest | null>(null);
  const [dragOffset, setDragOffset] = useState<number>(0); // Track where user grabbed the quest
  const [dragOverSlot, setDragOverSlot] = useState<{ date: string; time: string } | null>(null);
  const [resizingQuest, setResizingQuest] = useState<{ questId: string; handle: 'start' | 'end'; date: string } | null>(null);
  const [resizePreviewTime, setResizePreviewTime] = useState<string | null>(null);
  const [lastResizeAt, setLastResizeAt] = useState<number>(0);
  const getInitialHourHeight = () => {
    if (typeof window !== 'undefined' && 'matchMedia' in window) {
      try {
        return window.matchMedia('(min-width: 640px)').matches ? 80 : 64;
      } catch {
        return 80;
      }
    }
    return 80;
  };
  const [hourHeight, setHourHeight] = useState<number>(getInitialHourHeight());

  // Keep pixel-per-hour consistent with Tailwind heights (h-16 on mobile = 64px, h-20 on sm+ = 80px)
  useEffect(() => {
    const mql = window.matchMedia('(min-width: 640px)');
    const compute = () => setHourHeight(mql.matches ? 80 : 64);
    compute();
    if (mql.addEventListener) {
      mql.addEventListener('change', compute);
      return () => mql.removeEventListener('change', compute);
    } else {
      // Safari fallback
      // @ts-ignore
      mql.addListener(compute);
      return () => {
        // @ts-ignore
        mql.removeListener(compute);
      };
    }
  }, []);

  // Generate hours (0-23)
  const hours = Array.from({ length: 24 }, (_, i) => i);

  // Get week days starting from currentDate
  const weekDays = useMemo(() => {
    const days = [];
    const startOfWeek = new Date(currentDate);
    startOfWeek.setDate(currentDate.getDate() - currentDate.getDay()); // Start on Sunday
    
    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      days.push(day);
    }
    return days;
  }, [currentDate]);

  // Format date to YYYY-MM-DD
  const formatDate = (date: Date): string => {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };

  // Format time to HH:MM
  const formatTime = (hour: number): string => {
    return `${String(hour).padStart(2, '0')}:00`;
  };

  const getPositionFromTimes = (start: string, end: string): { top: number; height: number } => {
    const [startHour, startMin] = start.split(':').map(Number);
    const [endHour, endMin] = end.split(':').map(Number);
    const startMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;
    const duration = Math.max(0, endMinutes - startMinutes);
    const top = (startMinutes / 60) * hourHeight;
    const height = Math.max((duration / 60) * hourHeight, 40);
    return { top, height };
  };

  // Check if a time slot is within program's preferred time range
  const isPreferredTimeSlot = (date: Date, hour: number): boolean => {
    const dayOfWeek = date.getDay(); // 0=Sunday, 1=Monday, etc.

    return programs.some(program => {
      // Check if program has time preferences
      if (!program.preferredTimeStart || !program.preferredTimeEnd || !program.availableDays) {
        return false;
      }

      // Check if this day is available
      if (!program.availableDays.includes(dayOfWeek)) {
        return false;
      }

      // Check if this hour is within preferred time range
      const [startHour] = program.preferredTimeStart.split(':').map(Number);
      const [endHour] = program.preferredTimeEnd.split(':').map(Number);

      return hour >= startHour && hour < endHour;
    });
  };

  // Calculate time from mouse position (15-minute precision)
  const getTimeFromPosition = (clientY: number, containerElement: HTMLElement): string => {
    const containerRect = containerElement.getBoundingClientRect();
    const scrollTop = containerElement.scrollTop || 0;
    
    // Calculate position relative to the container including scroll
    const relativeY = clientY - containerRect.top + scrollTop;
    const totalMinutes = (relativeY / hourHeight) * 60;
    
    // Snap to 15-minute intervals
    const snappedMinutes = Math.round(totalMinutes / 15) * 15;
    const hour = Math.floor(snappedMinutes / 60);
    const minute = snappedMinutes % 60;
    
    // Clamp to valid time range
    const clampedHour = Math.max(0, Math.min(hour, 23));
    const clampedMinute = clampedHour === 23 ? Math.min(minute, 45) : minute;
    
    return `${String(clampedHour).padStart(2, '0')}:${String(clampedMinute).padStart(2, '0')}`;
  };

  // Get quests for a specific day
  const getQuestsForDay = (date: Date): Quest[] => {
    const dateStr = formatDate(date);
    return quests.filter(q => q.scheduledFor === dateStr && q.scheduledTime);
  };

  // Calculate quest position on timeline
  const getQuestPosition = (quest: Quest): { top: number; height: number } | null => {
    if (!quest.scheduledTime) return null;
    
    const [startHour, startMin] = quest.scheduledTime.start.split(':').map(Number);
    const [endHour, endMin] = quest.scheduledTime.end.split(':').map(Number);
    
    const startMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;
    const duration = endMinutes - startMinutes;
    
    const top = (startMinutes / 60) * hourHeight;
    const height = (duration / 60) * hourHeight;
    
    return { top, height: Math.max(height, 40) }; // Minimum 40px height
  };

  // Get color based on quest type/difficulty
  const getQuestColor = (quest: Quest): string => {
    // Check if quest has a custom color
    if (quest.customColor) {
      const customColors: Record<string, string> = {
        blue: 'from-blue-600 to-blue-700 border-blue-500',
        purple: 'from-purple-600 to-purple-700 border-purple-500',
        green: 'from-green-600 to-green-700 border-green-500',
        red: 'from-red-600 to-red-700 border-red-500',
        yellow: 'from-yellow-600 to-orange-600 border-yellow-500',
        cyan: 'from-cyan-600 to-cyan-700 border-cyan-500',
        pink: 'from-pink-600 to-pink-700 border-pink-500',
        gray: 'from-gray-700 to-gray-800 border-gray-600',
      };
      if (customColors[quest.customColor]) {
        return customColors[quest.customColor];
      }
    }
    
    // Default to type-based colors
    const colors = {
      daily: 'from-blue-600 to-blue-700 border-blue-500',
      main: 'from-purple-600 to-purple-700 border-purple-500',
      instant: 'from-green-600 to-green-700 border-green-500',
      boss: 'from-red-600 to-red-700 border-red-500',
    };
    return colors[quest.type] || colors.daily;
  };

  // Handle drag start - capture where user grabbed the quest
  const handleDragStart = (e: React.DragEvent, quest: Quest) => {
    setDraggedQuest(quest);
    e.dataTransfer.effectAllowed = 'move';
    
    // Calculate offset from quest start position
    if (quest.scheduledTime && timelineRef.current) {
      const [startHour, startMin] = quest.scheduledTime.start.split(':').map(Number);
      const questStartMinutes = startHour * 60 + startMin;
      const questStartPixels = (questStartMinutes / 60) * hourHeight;
      
      // Get mouse position relative to timeline
      const timelineRect = timelineRef.current.getBoundingClientRect();
      const scrollTop = timelineRef.current.scrollTop || 0;
      const mouseY = e.clientY - timelineRect.top + scrollTop;
      
      // Store offset from quest start
      setDragOffset(mouseY - questStartPixels);
    } else {
      setDragOffset(0);
    }
  };

  // Handle drag over with minute precision
  const handleDragOver = (e: React.DragEvent, date: string, containerElement: HTMLElement) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    // Calculate mouse position
    const containerRect = containerElement.getBoundingClientRect();
    const scrollTop = containerElement.scrollTop || 0;
    const mouseY = e.clientY - containerRect.top + scrollTop;
    
    // Subtract the offset to get where the quest START should be
    const adjustedY = mouseY - dragOffset;
    
    // Convert adjusted position to time
    const totalMinutes = (adjustedY / hourHeight) * 60;
    const snappedMinutes = Math.round(totalMinutes / 15) * 15;
    const hour = Math.floor(snappedMinutes / 60);
    const minute = snappedMinutes % 60;
    
    const clampedHour = Math.max(0, Math.min(hour, 23));
    const clampedMinute = clampedHour === 23 ? Math.min(minute, 45) : minute;
    const time = `${String(clampedHour).padStart(2, '0')}:${String(clampedMinute).padStart(2, '0')}`;
    
    setDragOverSlot({ date, time });
  };

  // Handle drop with minute precision
  const handleDrop = (e: React.DragEvent, date: string, containerElement: HTMLElement) => {
    e.preventDefault();
    if (draggedQuest) {
      // Calculate mouse position
      const containerRect = containerElement.getBoundingClientRect();
      const scrollTop = containerElement.scrollTop || 0;
      const mouseY = e.clientY - containerRect.top + scrollTop;
      
      // Subtract the offset to get where the quest START should be
      const adjustedY = mouseY - dragOffset;
      
      // Convert adjusted position to time
      const totalMinutes = (adjustedY / hourHeight) * 60;
      const snappedMinutes = Math.round(totalMinutes / 15) * 15;
      const hour = Math.floor(snappedMinutes / 60);
      const minute = snappedMinutes % 60;
      
      const clampedHour = Math.max(0, Math.min(hour, 23));
      const clampedMinute = clampedHour === 23 ? Math.min(minute, 45) : minute;
      const time = `${String(clampedHour).padStart(2, '0')}:${String(clampedMinute).padStart(2, '0')}`;
      
      onQuestDragEnd(draggedQuest.id, date, time);
    }
    setDraggedQuest(null);
    setDragOffset(0);
    setDragOverSlot(null);
  };

  // Handle resize start
  const handleResizeStart = (e: React.MouseEvent, questId: string, handle: 'start' | 'end', date: string) => {
    e.stopPropagation();
    e.preventDefault();
    setResizingQuest({ questId, handle, date });
  };

  // Global mouse move handler for smooth resizing (commit on mouse up to reduce writes)
  useEffect(() => {
    if (!resizingQuest || !onQuestResize || !timelineRef.current) return;

    let rafId: number | null = null;
    let lastTime = '';

    const handleMouseMove = (e: MouseEvent) => {
      if (rafId !== null) return; // Skip if already scheduled

      rafId = requestAnimationFrame(() => {
        const quest = quests.find(q => q.id === resizingQuest.questId);
        if (!quest || !quest.scheduledTime || !timelineRef.current) {
          rafId = null;
          return;
        }
        
        const newTime = getTimeFromPosition(e.clientY, timelineRef.current);
        
        // Only update if time changed (avoid unnecessary updates)
        if (newTime !== lastTime) {
          lastTime = newTime;
          setResizePreviewTime(newTime);
        }
        
        rafId = null;
      });
    };

    const handleMouseUp = () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
      // Commit a single update with the final time to minimize writes
      const quest = quests.find(q => q.id === resizingQuest.questId);
      if (quest && quest.scheduledTime && lastTime) {
        if (resizingQuest.handle === 'start' && lastTime < quest.scheduledTime.end) {
          onQuestResize(quest.id, lastTime, quest.scheduledTime.end);
        }
        if (resizingQuest.handle === 'end' && lastTime > quest.scheduledTime.start) {
          onQuestResize(quest.id, quest.scheduledTime.start, lastTime);
        }
      }
      setResizingQuest(null);
      setResizePreviewTime(null);
      setLastResizeAt(Date.now());
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizingQuest, quests, onQuestResize]);

  const today = formatDate(new Date());

  return (
    <div 
      className="bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl overflow-hidden relative"
      style={{
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.4), inset 0 2px 4px rgba(255, 255, 255, 0.1), inset 0 -3px 8px rgba(0, 0, 0, 0.5)'
      }}
    >
      {/* Animated Background Effect */}
      <div className="absolute inset-0 opacity-5 pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-system-blue to-transparent animate-pulse"></div>
        <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-system-blue to-transparent animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      {/* Scrollable Container for mobile */}
      <div className="overflow-x-auto">
        <div className="min-w-[640px]">
          {/* Week Header */}
          <div 
            className="grid grid-cols-[60px_repeat(7,1fr)] sm:grid-cols-[80px_repeat(7,1fr)] border-b-2 border-b-slate-500/50 bg-gradient-to-b from-slate-800/60 via-slate-900/50 to-slate-950/60 backdrop-blur-sm relative z-10"
            style={{
              boxShadow: 'inset 0 2px 4px rgba(255, 255, 255, 0.05), inset 0 -2px 4px rgba(0, 0, 0, 0.3)'
            }}
          >
            {/* Time column header */}
            <div className="border-r-2 border-r-slate-500/50 p-2 sm:p-4"></div>
            
            {/* Day headers */}
            {weekDays.map((day, index) => {
              const dateStr = formatDate(day);
              const isToday = dateStr === today;
              const dayOfWeek = day.getDay();
              const dayKeys = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;
              const dayName = t.calendar.weekDays[dayKeys[dayOfWeek]];
              const dayNum = day.getDate();
              
              return (
                <div
                  key={index}
                  className={`
                    p-2 sm:p-4 text-center border-r-2 border-r-slate-500/40 relative
                    ${isToday ? 'bg-gradient-to-br from-purple-900/30 to-purple-950/30 border-r-purple-500/60' : ''}
                  `}
                >
                  {isToday && (
                    <div className="absolute top-1 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-system-purple rounded text-[8px] font-orbitron font-bold text-white shadow-lg shadow-purple-500/50">
                      TODAY
                    </div>
                  )}
                  <div className="text-[10px] sm:text-xs text-gray-400 font-rajdhani uppercase mb-0.5 sm:mb-1">
                    {dayName}
                  </div>
                  <div className={`
                    text-lg sm:text-2xl font-bold font-orbitron
                    ${isToday ? 'text-system-purple' : 'text-white'}
                  `}>
                    {dayNum}
                  </div>
                  {isToday && (
                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-system-purple to-transparent shadow-lg shadow-purple-500/50"></div>
                  )}
                </div>
              );
            })}
          </div>

      {/* Timeline Grid */}
      <div ref={timelineRef} className="overflow-y-auto max-h-[calc(100vh-300px)] relative">
        {/* Snap Indicator Line & Ghost Preview */}
        {dragOverSlot && draggedQuest && (
          <>
            {/* Snap Line */}
            <div
              className="absolute left-20 right-0 h-1 bg-system-blue shadow-lg shadow-blue-500/50 z-40 pointer-events-none"
              style={{
                top: `${(() => {
                  const [hour, min] = dragOverSlot.time.split(':').map(Number);
                  return ((hour * 60 + min) / 60) * hourHeight;
                })()}px`
              }}
            >
              <div className="absolute -left-2 -top-1.5 w-3 h-3 bg-system-blue rounded-full shadow-lg shadow-blue-500/70 animate-pulse"></div>
              <div className="absolute right-2 -top-4 px-3 py-1.5 bg-system-blue backdrop-blur-sm text-white text-xs font-orbitron font-bold rounded-lg shadow-lg shadow-blue-500/50 border border-blue-400">
                📍 {dragOverSlot.time}
              </div>
            </div>
            
            {/* Ghost Preview of Quest Block */}
            {draggedQuest.scheduledTime && (
              <div
                className="absolute left-20 right-0 z-30 pointer-events-none"
                style={{
                  top: `${(() => {
                    const [hour, min] = dragOverSlot.time.split(':').map(Number);
                    return ((hour * 60 + min) / 60) * hourHeight;
                  })()}px`
                }}
              >
                <div className="absolute left-0 right-0 mx-1">
                  {weekDays.map((day, idx) => {
                    const dateStr = formatDate(day);
                    if (dateStr !== dragOverSlot.date) return null;
                    
                    const [startHour, startMin] = draggedQuest.scheduledTime!.start.split(':').map(Number);
                    const [endHour, endMin] = draggedQuest.scheduledTime!.end.split(':').map(Number);
                    const durationMinutes = (endHour * 60 + endMin) - (startHour * 60 + startMin);
                    const height = (durationMinutes / 60) * hourHeight;
                    
                    // Calculate which column this day is in
                    const columnOffset = idx * (100 / 7);
                    
                    return (
                      <div
                        key={dateStr}
                        className="absolute bg-system-blue/30 border-2 border-system-blue border-dashed rounded animate-pulse"
                        style={{
                          left: `calc(${columnOffset}%)`,
                          width: `calc(${100 / 7}% - 8px)`,
                          height: `${height}px`,
                        }}
                      >
                        <div className="p-2 flex flex-col h-full opacity-70">
                          <div className="text-xs font-bold font-orbitron text-white truncate">
                            {draggedQuest.title}
                          </div>
                          <div className="text-[10px] text-white/80 font-rajdhani">
                            {dragOverSlot.time} - {(() => {
                              const [h, m] = dragOverSlot.time.split(':').map(Number);
                              const endMinutes = h * 60 + m + durationMinutes;
                              const eh = Math.floor(endMinutes / 60);
                              const em = endMinutes % 60;
                              return `${String(eh).padStart(2, '0')}:${String(em).padStart(2, '0')}`;
                            })()}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}

        <div className="grid grid-cols-[60px_repeat(7,1fr)] sm:grid-cols-[80px_repeat(7,1fr)]">
          {/* Hours */}
          {hours.map((hour) => (
            <React.Fragment key={hour}>
              {/* Time label */}
              <div 
                className="h-16 sm:h-20 border-r-2 border-r-slate-500/50 border-b border-b-slate-500/40 bg-gradient-to-b from-slate-800/50 via-slate-900/40 to-slate-950/50 backdrop-blur-sm flex items-start justify-center pt-1 sm:pt-2"
                style={{
                  boxShadow: 'inset -2px 0 4px rgba(0, 0, 0, 0.3)'
                }}
              >
                <span className="text-[10px] sm:text-xs font-rajdhani text-gray-400">
                  {formatTime(hour)}
                </span>
              </div>
              
              {/* Day columns */}
              {weekDays.map((day, dayIndex) => {
                const dateStr = formatDate(day);
                const isToday = dateStr === today;
                const isDragOver = dragOverSlot?.date === dateStr;
                const isPreferred = isPreferredTimeSlot(day, hour);

                return (
                  <div
                    key={`${hour}-${dayIndex}`}
                    className={`
                      h-16 sm:h-20 border-r-2 border-r-slate-500/40 border-b border-b-slate-500/40 relative
                      hover:bg-blue-500/10 cursor-pointer transition-colors
                      ${isToday ? 'bg-purple-500/5 border-r-purple-500/50' : ''}
                      ${isDragOver ? 'bg-blue-500/15' : ''}
                      ${isPreferred ? 'bg-green-500/5 border-l-2 border-l-green-500/30' : ''}
                    `}
                    onClick={() => onTimeSlotClick(dateStr, formatTime(hour))}
                    onDragOver={(e) => timelineRef.current && handleDragOver(e, dateStr, timelineRef.current)}
                    onDrop={(e) => timelineRef.current && handleDrop(e, dateStr, timelineRef.current)}
                  >
                    {/* Quest blocks */}
                    {hour === 0 && getQuestsForDay(day).map((quest) => {
                      let position = getQuestPosition(quest);
                      if (!position) return null;
                      
                      const isCompleted = quest.status === 'completed';
                      const isFailed = quest.status === 'failed';
                      
                      // Live preview while resizing without writes
                      if (resizingQuest?.questId === quest.id && resizePreviewTime && quest.scheduledTime) {
                        const previewStart = resizingQuest.handle === 'start' ? resizePreviewTime : quest.scheduledTime.start;
                        const previewEnd = resizingQuest.handle === 'end' ? resizePreviewTime : quest.scheduledTime.end;
                        position = getPositionFromTimes(previewStart, previewEnd);
                      }

                      return (
                        <div
                          key={quest.id}
                          draggable
                          onDragStart={(e) => handleDragStart(e, quest)}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (Date.now() - lastResizeAt < 300) return; // ignore click right after resize
                            onQuestClick(quest);
                          }}
                          className={`
                            absolute left-1 right-1 rounded border-2 group
                            bg-gradient-to-br ${getQuestColor(quest)}
                            hover:shadow-neon-blue hover:scale-[1.02] transition-all cursor-move
                            ${isCompleted ? 'opacity-70' : ''}
                            ${isFailed ? 'opacity-40' : ''}
                            ${draggedQuest?.id === quest.id ? 'opacity-30' : ''} 
                            before:absolute before:inset-0 before:rounded before:bg-white before:opacity-0 
                            hover:before:opacity-10 before:transition-opacity
                          `}
                          style={{
                            top: `${position.top}px`,
                            height: `${position.height}px`,
                            zIndex: 10,
                            transition: resizingQuest?.questId === quest.id ? 'top 90ms cubic-bezier(0.2, 0.6, 0.2, 1), height 90ms cubic-bezier(0.2, 0.6, 0.2, 1)' : undefined,
                            willChange: resizingQuest?.questId === quest.id ? 'top, height' : undefined,
                          }}
                        >
                          {/* Resize Handle - Top */}
                          <div
                            className="absolute top-0 left-0 right-0 h-3 cursor-ns-resize hover:bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity z-20"
                            onMouseDown={(e) => handleResizeStart(e, quest.id, 'start', dateStr)}
                            draggable={false}
                          >
                            <div className="absolute -top-1 left-1/2 -translate-x-1/2 text-white/80 text-[10px] leading-none pointer-events-none select-none">↕</div>
                            <div className="absolute top-1 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-white/40 rounded"></div>
                          </div>

                          {/* Quest Content - Google Calendar style for week view */}
                          <div 
                            className="flex flex-col h-full overflow-hidden pointer-events-none"
                            title={`${quest.title} (${quest.scheduledTime?.start} - ${quest.scheduledTime?.end})`}
                          >
                            {/* Tiny blocks (< 30px) - Super minimal */}
                            {position.height < 30 && (
                              <div className="flex items-center h-full px-0.5">
                                <div className="text-[6px] leading-tight font-bold font-orbitron text-white truncate flex-1">
                                  {quest.title}
                                </div>
                                {isCompleted && <span className="text-green-300 text-[7px]">✓</span>}
                                {isFailed && <span className="text-red-300 text-[7px]">✕</span>}
                              </div>
                            )}
                            
                            {/* Very small blocks (30-45px) - Title only, ultra compact */}
                            {position.height >= 30 && position.height < 45 && (
                              <div className="flex items-start h-full px-0.5 py-0.5 gap-0.5">
                                <div className={`
                                  text-[8px] leading-tight font-bold font-orbitron text-white flex-1 line-clamp-2
                                  ${isCompleted ? 'line-through' : ''}
                                `}>
                                  {quest.title}
                                </div>
                                {isCompleted && <span className="text-green-300 text-[9px] flex-shrink-0">✓</span>}
                                {isFailed && <span className="text-red-300 text-[9px] flex-shrink-0">✕</span>}
                              </div>
                            )}
                            
                            {/* Small blocks (45-60px) - Title with wrapped text */}
                            {position.height >= 45 && position.height < 60 && (
                              <div className="flex items-start h-full px-0.5 py-0.5 gap-0.5">
                                <div className={`
                                  text-[9px] leading-tight font-bold font-orbitron text-white flex-1 line-clamp-3
                                  ${isCompleted ? 'line-through' : ''}
                                `}>
                                  {quest.title}
                                </div>
                                {isCompleted && <span className="text-green-300 text-[10px] flex-shrink-0">✓</span>}
                                {isFailed && <span className="text-red-300 text-[10px] flex-shrink-0">✕</span>}
                              </div>
                            )}
                            
                            {/* Medium blocks (60-80px) - Title and time, compact */}
                            {position.height >= 60 && position.height < 80 && (
                              <div className="flex flex-col h-full justify-start px-1 py-0.5">
                                <div className="flex items-start justify-between gap-0.5 mb-0.5">
                                  <div className={`
                                    text-[10px] leading-tight font-bold font-orbitron text-white flex-1 line-clamp-2
                                    ${isCompleted ? 'line-through' : ''}
                                  `}>
                                    {quest.title}
                                  </div>
                                  {isCompleted && <span className="text-green-300 text-xs flex-shrink-0">✓</span>}
                                  {isFailed && <span className="text-red-300 text-xs flex-shrink-0">✕</span>}
                                </div>
                                <div className="text-[8px] leading-tight text-white/70 font-rajdhani">
                                  {quest.scheduledTime?.start}
                                </div>
                              </div>
                            )}
                            
                            {/* Large blocks (>= 80px) - More details */}
                            {position.height >= 80 && (
                              <div className="flex flex-col h-full px-1 py-1">
                                <div className="flex items-start justify-between gap-0.5 mb-0.5">
                                  <div className="flex-1 min-w-0">
                                    <div className={`
                                      text-[10px] leading-tight font-bold font-orbitron text-white line-clamp-2
                                      ${isCompleted ? 'line-through' : ''}
                                    `}>
                                      {quest.title}
                                    </div>
                                    <div className="text-[9px] leading-tight text-white/70 font-rajdhani mt-0.5">
                                      {quest.scheduledTime?.start} - {quest.scheduledTime?.end}
                                    </div>
                                  </div>
                                  {isCompleted && <span className="text-green-300 text-xs flex-shrink-0">✓</span>}
                                  {isFailed && <span className="text-red-300 text-xs flex-shrink-0">✕</span>}
                                </div>
                                
                                {position.height > 100 && quest.description && (
                                  <div className="text-[9px] leading-tight text-white/70 mb-0.5 line-clamp-2">
                                    {quest.description}
                                  </div>
                                )}
                                
                                {/* Rewards removed per request: keep name/time only */}
                              </div>
                            )}
                          </div>

                          {/* Resize Handle - Bottom */}
                          <div
                            className="absolute bottom-0 left-0 right-0 h-3 cursor-ns-resize hover:bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity z-20"
                            onMouseDown={(e) => handleResizeStart(e, quest.id, 'end', dateStr)}
                            draggable={false}
                          >
                            <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 text-white/80 text-[10px] leading-none pointer-events-none select-none">↕</div>
                            <div className="absolute bottom-1 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-white/40 rounded"></div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Unscheduled Quests Section */}
      <div 
        className="border-t-2 border-t-slate-600/30 bg-gradient-to-b from-slate-800/60 via-slate-900/50 to-slate-950/60 backdrop-blur-sm p-3 sm:p-4"
        style={{
          boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.3)'
        }}
      >
        <h3 className="text-xs sm:text-sm font-bold font-orbitron text-gray-400 uppercase mb-2 sm:mb-3 tracking-wide">
          📌 {t.calendar.unscheduledQuests}
        </h3>
        <div className="flex gap-1.5 sm:gap-2 flex-wrap">
          {quests
            .filter(q => !q.scheduledTime && q.status === 'active')
            .map(quest => (
              <button
                key={quest.id}
                draggable
                onDragStart={(e) => handleDragStart(e, quest)}
                onClick={() => onQuestClick(quest)}
                className={`
                  px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg border-2 bg-gradient-to-br ${getQuestColor(quest)}
                  hover:shadow-lg hover:shadow-blue-500/30 hover:scale-105 transition-all text-[10px] sm:text-xs font-orbitron font-bold text-white
                `}
              >
                {quest.title}
              </button>
            ))}
          {quests.filter(q => !q.scheduledTime && q.status === 'active').length === 0 && (
            <p className="text-xs text-gray-500 font-rajdhani italic">
              No unscheduled quests. All your quests are planned! 🎯
            </p>
          )}
        </div>
      </div>
        </div>
      </div>
    </div>
  );
};

export default WeekView;

